<template>
  <div
    v-bind="$attrs"
    v-on="$listeners"
    v-click-outside="onClickOutside"
    :style="style.root"
  >
    <div
      :disabled="disabled"
      :error="error"
      :focus="focus"
      :class="$style.container"
      :style="style.container"
    >
      <div
        :disabled="disabled"
        :error="error"
        :focus="focus"
        :class="$style.header"
        :style="style.header"
      >
        <slot
          name="header"
          :error="error"
          :focus="focus"
          :input="input"
          :inputLengthMax="inputLengthMax"
        />
      </div>
      <textarea
        v-model="input"
        ref="textarea"
        :disabled="disabled"
        :error="error"
        :focus="focus"
        @blur="() => { focus = false; }"
        @focus="() => { focus = true; }"
        :class="$style.textarea"
        :style="style.textarea"
      />
      <div
        :disabled="disabled"
        :error="error"
        :focus="focus"
        :class="$style.footer"
        :style="style.footer"
      >
        <slot
          name="footer"
          :error="error"
          :focus="focus"
          :input="input"
          :inputLengthMax="inputLengthMax"
        />
      </div>
    </div>
  </div>
</template>

<script>
  import ClickOutside from 'vue-click-outside'
  import { CssStyleProcessor } from '@js/vue-style-utils'

  export default {
    name: 'BaseTextarea',
    inheritAttrs: false,
    model: {
      prop: 'vmodelValue',
      event: 'change-input',
    },
    directives: {
      ClickOutside,
    },
    props: {
      vmodelValue: {
        type: String,
        required: true,
      },
      disabled: {
        type: Boolean,
        required: false,
        default: false,
      },
      inputLengthMax: {
        type: [Number, Object],
        required: false,
        default: null,
      },
      styleConfig: {
        type: Object,
        required: false,
        default: () => {},
      },
    },
    data: function () {
      return {
        focus: false,
        hover: false,
        input: this.vmodelValue,
        style: {
          container: {},
          footer: {},
          header: {},
          root: {},
          textarea: {
            height: '100%',
            outline: 'none',
            width: '100%',
          },
        },
      };
    },
    created: function () {
      const proc = new CssStyleProcessor(
        '--v2-base-textarea',
        ['focus', 'error',],
      );

      Object.assign(
        this.style.container,
        proc.getExtendedStyle(this.styleConfig.container, 'container'),
      );
      Object.assign(
        this.style.footer,
        proc.getExtendedStyle(this.styleConfig.footer, 'footer'),
      );
      Object.assign(
        this.style.header,
        proc.getExtendedStyle(this.styleConfig.header, 'header'),
      );
      Object.assign(
        this.style.textarea,
        proc.getExtendedStyle(this.styleConfig.textarea, 'textarea'),
      );

      Object.assign(
        this.style.root,
        this.styleConfig.root,
      );
    },
    mounted: function () {
      this.focus = document.activeElement === this.$refs.textarea;
    },
    computed: {
      error: function () {
        if (this.inputLengthMax === null) {
          return false;
        }
        return this.inputLengthMax === null
          ? false
          : this.input.length > this.inputLengthMax;
      },
    },
    methods: {
      onClickOutside: function (e) {
        this.$emit('click-outside', e);
      },
    },
    watch: {
      vmodelValue: function (newVal, oldVal) {
        this.input = newVal;
      },
      error: function (newVal, oldVal) {
        if (newVal) {
          this.$emit('error', this.input);
        }
      },
      input: function (newVal, oldVal) {
        this.$emit('change-input', newVal);
      },
    },
  };
</script>

<style lang="stylus" module>
  .container
    background-color var(--v2-base-textarea-container-background-color)
    border var(--v2-base-textarea-container-border)
    border-radius var(--v2-base-textarea-container-border-radius)
    border-width var(--v2-base-textarea-container-border-width)
    box-shadow var(--v2-base-textarea-container-box-shadow)
    &:hover
      background-color var(--v2-base-textarea-container-background-color-hover)
      border var(--v2-base-textarea-container-border-hover)
      border-radius var(--v2-base-textarea-container-border-radius-hover)
      border-width var(--v2-base-textarea-container-border-width-hover)
      box-shadow var(--v2-base-textarea-container-box-shadow-hover)
    &:active
      background-color var(--v2-base-textarea-container-background-color-active)
      border var(--v2-base-textarea-container-border-active)
      border-radius var(--v2-base-textarea-container-border-radius-active)
      border-width var(--v2-base-textarea-container-border-width-active)
      box-shadow var(--v2-base-textarea-container-box-shadow-active)
    &[focus]
      background-color var(--v2-base-textarea-container-background-color-focus)
      border var(--v2-base-textarea-container-border-focus)
      border-radius var(--v2-base-textarea-container-border-radius-focus)
      border-width var(--v2-base-textarea-container-border-width-focus)
      box-shadow var(--v2-base-textarea-container-box-shadow-focus)
    &[error]
      background-color var(--v2-base-textarea-container-background-color-error)
      border var(--v2-base-textarea-container-border-error)
      border-radius var(--v2-base-textarea-container-border-radius-error)
      border-width var(--v2-base-textarea-container-border-width-error)
      box-shadow var(--v2-base-textarea-container-box-shadow-error)
    &[disabled]
      background-color var(--v2-base-textarea-container-background-color-disabled)
      border var(--v2-base-textarea-container-border-disabled)
      border-radius var(--v2-base-textarea-container-border-radius-disabled)
      border-width var(--v2-base-textarea-container-border-width-disabled)
      box-shadow var(--v2-base-textarea-container-box-shadow-disabled)

  .footer
    background-color var(--v2-base-textarea-footer-background-color)
    border var(--v2-base-textarea-footer-border)
    border-radius var(--v2-base-textarea-footer-border-radius)
    border-width var(--v2-base-textarea-footer-border-width)
    color var(--v2-base-textarea-footer-color)
    cursor var(--v2-base-textarea-footer-cursor)
    font-family var(--v2-base-textarea-footer-font-family)
    font-size var(--v2-base-textarea-footer-font-size)
    font-weight var(--v2-base-textarea-footer-font-weight)
    &:hover
      background-color var(--v2-base-textarea-footer-background-color-hover)
      border var(--v2-base-textarea-footer-border-hover)
      border-radius var(--v2-base-textarea-footer-border-radius-hover)
      border-width var(--v2-base-textarea-footer-border-width-hover)
      color var(--v2-base-textarea-footer-color-hover)
      cursor var(--v2-base-textarea-footer-cursor-hover)
      font-family var(--v2-base-textarea-footer-font-family-hover)
      font-size var(--v2-base-textarea-footer-font-size-hover)
      font-weight var(--v2-base-textarea-footer-font-weight-hover)
    &:active
      background-color var(--v2-base-textarea-footer-background-color-active)
      border var(--v2-base-textarea-footer-border-active)
      border-radius var(--v2-base-textarea-footer-border-radius-active)
      border-width var(--v2-base-textarea-footer-border-width-active)
      color var(--v2-base-textarea-footer-color-active)
      cursor var(--v2-base-textarea-footer-cursor-active)
      font-family var(--v2-base-textarea-footer-font-family-active)
      font-size var(--v2-base-textarea-footer-font-size-active)
      font-weight var(--v2-base-textarea-footer-font-weight-active)
    &[focus]
      background-color var(--v2-base-textarea-footer-background-color-focus)
      border var(--v2-base-textarea-footer-border-focus)
      border-radius var(--v2-base-textarea-footer-border-radius-focus)
      border-width var(--v2-base-textarea-footer-border-width-focus)
      color var(--v2-base-textarea-footer-color-focus)
      cursor var(--v2-base-textarea-footer-cursor-focus)
      font-family var(--v2-base-textarea-footer-font-family-focus)
      font-size var(--v2-base-textarea-footer-font-size-focus)
      font-weight var(--v2-base-textarea-footer-font-weight-focus)
    &[error]
      background-color var(--v2-base-textarea-footer-background-color-error)
      border var(--v2-base-textarea-footer-border-error)
      border-radius var(--v2-base-textarea-footer-border-radius-error)
      border-width var(--v2-base-textarea-footer-border-width-error)
      color var(--v2-base-textarea-footer-color-error)
      cursor var(--v2-base-textarea-footer-cursor-error)
      font-family var(--v2-base-textarea-footer-font-family-error)
      font-size var(--v2-base-textarea-footer-font-size-error)
      font-weight var(--v2-base-textarea-footer-font-weight-error)
    &[disabled]
      background-color var(--v2-base-textarea-footer-background-color-disabled)
      border var(--v2-base-textarea-footer-border-disabled)
      border-radius var(--v2-base-textarea-footer-border-radius-disabled)
      border-width var(--v2-base-textarea-footer-border-width-disabled)
      color var(--v2-base-textarea-footer-color-disabled)
      cursor var(--v2-base-textarea-footer-cursor-disabled)
      font-family var(--v2-base-textarea-footer-font-family-disabled)
      font-size var(--v2-base-textarea-footer-font-size-disabled)
      font-weight var(--v2-base-textarea-footer-font-weight-disabled)

  .header
    background-color var(--v2-base-textarea-header-background-color)
    border var(--v2-base-textarea-header-border)
    border-radius var(--v2-base-textarea-header-border-radius)
    border-width var(--v2-base-textarea-header-border-width)
    color var(--v2-base-textarea-header-color)
    cursor var(--v2-base-textarea-header-cursor)
    font-family var(--v2-base-textarea-header-font-family)
    font-size var(--v2-base-textarea-header-font-size)
    font-weight var(--v2-base-textarea-header-font-weight)
    &:hover
      background-color var(--v2-base-textarea-header-background-color-hover)
      border var(--v2-base-textarea-header-border-hover)
      border-radius var(--v2-base-textarea-header-border-radius-hover)
      border-width var(--v2-base-textarea-header-border-width-hover)
      color var(--v2-base-textarea-header-color-hover)
      cursor var(--v2-base-textarea-header-cursor-hover)
      font-family var(--v2-base-textarea-header-font-family-hover)
      font-size var(--v2-base-textarea-header-font-size-hover)
      font-weight var(--v2-base-textarea-header-font-weight-hover)
    &:active
      background-color var(--v2-base-textarea-header-background-color-active)
      border var(--v2-base-textarea-header-border-active)
      border-radius var(--v2-base-textarea-header-border-radius-active)
      border-width var(--v2-base-textarea-header-border-width-active)
      color var(--v2-base-textarea-header-color-active)
      cursor var(--v2-base-textarea-header-cursor-active)
      font-family var(--v2-base-textarea-header-font-family-active)
      font-size var(--v2-base-textarea-header-font-size-active)
      font-weight var(--v2-base-textarea-header-font-weight-active)
    &[focus]
      background-color var(--v2-base-textarea-header-background-color-focus)
      border var(--v2-base-textarea-header-border-focus)
      border-radius var(--v2-base-textarea-header-border-radius-focus)
      border-width var(--v2-base-textarea-header-border-width-focus)
      color var(--v2-base-textarea-header-color-focus)
      cursor var(--v2-base-textarea-header-cursor-focus)
      font-family var(--v2-base-textarea-header-font-family-focus)
      font-size var(--v2-base-textarea-header-font-size-focus)
      font-weight var(--v2-base-textarea-header-font-weight-focus)
    &[error]
      background-color var(--v2-base-textarea-header-background-color-error)
      border var(--v2-base-textarea-header-border-error)
      border-radius var(--v2-base-textarea-header-border-radius-error)
      border-width var(--v2-base-textarea-header-border-width-error)
      color var(--v2-base-textarea-header-color-error)
      cursor var(--v2-base-textarea-header-cursor-error)
      font-family var(--v2-base-textarea-header-font-family-error)
      font-size var(--v2-base-textarea-header-font-size-error)
      font-weight var(--v2-base-textarea-header-font-weight-error)
    &[disabled]
      background-color var(--v2-base-textarea-header-background-color-disabled)
      border var(--v2-base-textarea-header-border-disabled)
      border-radius var(--v2-base-textarea-header-border-radius-disabled)
      border-width var(--v2-base-textarea-header-border-width-disabled)
      color var(--v2-base-textarea-header-color-disabled)
      cursor var(--v2-base-textarea-header-cursor-disabled)
      font-family var(--v2-base-textarea-header-font-family-disabled)
      font-size var(--v2-base-textarea-header-font-size-disabled)
      font-weight var(--v2-base-textarea-header-font-weight-disabled)

  .textarea
    background-color var(--v2-base-textarea-textarea-background-color)
    border var(--v2-base-textarea-textarea-border)
    border-radius var(--v2-base-textarea-textarea-border-radius)
    border-width var(--v2-base-textarea-textarea-border-width)
    color var(--v2-base-textarea-textarea-color)
    cursor var(--v2-base-textarea-textarea-cursor)
    font-family var(--v2-base-textarea-textarea-font-family)
    font-size var(--v2-base-textarea-textarea-font-size)
    font-weight var(--v2-base-textarea-textarea-font-weight)
    resize none
    &:hover
      background-color var(--v2-base-textarea-textarea-background-color-hover)
      border var(--v2-base-textarea-textarea-border-hover)
      border-radius var(--v2-base-textarea-textarea-border-radius-hover)
      border-width var(--v2-base-textarea-textarea-border-width-hover)
      color var(--v2-base-textarea-textarea-color-hover)
      cursor var(--v2-base-textarea-textarea-cursor-hover)
      font-family var(--v2-base-textarea-textarea-font-family-hover)
      font-size var(--v2-base-textarea-textarea-font-size-hover)
      font-weight var(--v2-base-textarea-textarea-font-weight-hover)
      resize none
    &:active
      background-color var(--v2-base-textarea-textarea-background-color-active)
      border var(--v2-base-textarea-textarea-border-active)
      border-radius var(--v2-base-textarea-textarea-border-radius-active)
      border-width var(--v2-base-textarea-textarea-border-width-active)
      color var(--v2-base-textarea-textarea-color-active)
      cursor var(--v2-base-textarea-textarea-cursor-active)
      font-family var(--v2-base-textarea-textarea-font-family-active)
      font-size var(--v2-base-textarea-textarea-font-size-active)
      font-weight var(--v2-base-textarea-textarea-font-weight-active)
      resize none
    &[focus]
      background-color var(--v2-base-textarea-textarea-background-color-focus)
      border var(--v2-base-textarea-textarea-border-focus)
      border-radius var(--v2-base-textarea-textarea-border-radius-focus)
      border-width var(--v2-base-textarea-textarea-border-width-focus)
      color var(--v2-base-textarea-textarea-color-focus)
      cursor var(--v2-base-textarea-textarea-cursor-focus)
      font-family var(--v2-base-textarea-textarea-font-family-focus)
      font-size var(--v2-base-textarea-textarea-font-size-focus)
      font-weight var(--v2-base-textarea-textarea-font-weight-focus)
      resize none
    &[error]
      background-color var(--v2-base-textarea-textarea-background-color-error)
      border var(--v2-base-textarea-textarea-border-error)
      border-radius var(--v2-base-textarea-textarea-border-radius-error)
      border-width var(--v2-base-textarea-textarea-border-width-error)
      color var(--v2-base-textarea-textarea-color-error)
      cursor var(--v2-base-textarea-textarea-cursor-error)
      font-family var(--v2-base-textarea-textarea-font-family-error)
      font-size var(--v2-base-textarea-textarea-font-size-error)
      font-weight var(--v2-base-textarea-textarea-font-weight-error)
      resize none
    &[disabled]
      background-color var(--v2-base-textarea-textarea-background-color-disabled)
      border var(--v2-base-textarea-textarea-border-disabled)
      border-radius var(--v2-base-textarea-textarea-border-radius-disabled)
      border-width var(--v2-base-textarea-textarea-border-width-disabled)
      color var(--v2-base-textarea-textarea-color-disabled)
      cursor var(--v2-base-textarea-textarea-cursor-disabled)
      font-family var(--v2-base-textarea-textarea-font-family-disabled)
      font-size var(--v2-base-textarea-textarea-font-size-disabled)
      font-weight var(--v2-base-textarea-textarea-font-weight-disabled)
      resize none
</style>
